/**
 * Event Bus - Sistema de eventos desacoplado para comunicación entre componentes
 *
 * Uso:
 * - Emitir: eventBus.emit('event-name', data)
 * - Escuchar: eventBus.on('event-name', callback)
 * - Dejar de escuchar: eventBus.off('event-name', callback)
 */

import logger from './simpleLogger';

type EventCallback = (data?: any) => void;

class EventBus {
  private events: Map<string, EventCallback[]>;

  constructor() {
    this.events = new Map();
  }

  /**
   * Registrar un listener para un evento
   */
  on(event: string, callback: EventCallback): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(callback);

    if (import.meta.env.DEV) {
      logger.info(`📡 EventBus: Listener registrado para "${event}"`);
    }
  }

  /**
   * Remover un listener de un evento
   */
  off(event: string, callback: EventCallback): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        if (import.meta.env.DEV) {
          logger.info(`📡 EventBus: Listener removido de "${event}"`);
        }
      }
    }
  }

  /**
   * Emitir un evento con datos opcionales
   */
  emit(event: string, data?: any): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      if (import.meta.env.DEV) {
        logger.info(`📡 EventBus: Emitiendo "${event}"`, data);
      }
      callbacks.forEach((callback) => callback(data));
    } else {
      // Algunos eventos son opcionales y no siempre tienen listeners
      // Solo mostrar warning para eventos que realmente deberían tener listeners
      const optionalEvents = [
        'drive-filters-from-mentions',
        'documents-auto-selected',
        'drive-filters-to-mentions',
        'open-document-panel',
        'document-suggestions-updated',
      ];

      if (import.meta.env.DEV && !optionalEvents.includes(event)) {
        console.warn(`📡 EventBus: No hay listeners para "${event}"`);
      }
    }
  }

  /**
   * Remover todos los listeners de un evento
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event);
      if (import.meta.env.DEV) {
        logger.info(`📡 EventBus: Todos los listeners removidos de "${event}"`);
      }
    } else {
      this.events.clear();
      if (import.meta.env.DEV) {
        logger.info(`📡 EventBus: Todos los listeners removidos`);
      }
    }
  }

  /**
   * Obtener el número de listeners para un evento
   */
  listenerCount(event: string): number {
    return this.events.get(event)?.length || 0;
  }
}

// Singleton instance
export const eventBus = new EventBus();

// Event names (para autocompletado y evitar typos)
export const EventNames = {
  // Guardados
  SAVED_ITEM_CREATED: 'saved-item-created',
  SAVED_ITEM_UPDATED: 'saved-item-updated',
  SAVED_ITEM_DELETED: 'saved-item-deleted',

  // Drive
  DRIVE_PANEL_OPEN_REQUESTED: 'drive-panel-open-requested',
  DRIVE_PANEL_TOGGLE_STATE: 'drive-panel-toggle-state', // Toggle entre colapsado, normal y fullscreen
  DRIVE_SEARCH_CHANGED: 'drive-search-changed', // Nueva búsqueda desde input area
  DRIVE_FILTERS_CHANGED: 'drive-filters-changed', // Cambios en filtros desde input area
  DRIVE_FILTERS_RESET: 'drive-filters-reset', // Reset de filtros desde input area
  DRIVE_FILTERS_FROM_MENTIONS: 'drive-filters-from-mentions', // Sincronizar filtros del Drive desde menciones
  DRIVE_FILTERS_TO_MENTIONS: 'drive-filters-to-mentions', // Sincronizar menciones desde filtros del Drive

  // Documentos
  DOCUMENT_UPLOADED: 'document-uploaded',
  DOCUMENT_DELETED: 'document-deleted',
  DOCUMENTS_AUTO_SELECTED: 'documents-auto-selected', // Documentos auto-seleccionados por el sistema
  DOCUMENT_SUGGESTIONS_UPDATED: 'document-suggestions-updated', // Sugerencias actualizadas (pre-select)

  // Studios
  STUDIO_GENERATED: 'studio-generated',

  // Chat
  MESSAGE_SENT: 'message-sent',
  MESSAGE_EDIT_REQUESTED: 'message-edit-requested',
  SUGGESTED_QUESTION_CLICKED: 'suggested-question-clicked', // Cuando se hace click en una pregunta sugerida
  INPUT_CHANGED: 'input-changed', // Cuando el usuario escribe en el input
  TOGGLE_CONTROL_PANEL: 'toggle-control-panel', // Toggle del panel de control overlay
  OPEN_DOCUMENT_PANEL: 'open-document-panel', // Abre el panel de documentos con tab específica
  SUGGESTED_QUESTIONS_TOGGLED: 'suggested-questions-toggled', // Toggle preguntas sugeridas
  SUGGESTED_TASKS_TOGGLED: 'suggested-tasks-toggled', // Toggle studios/tareas sugeridas

  // Conversations
  TOGGLE_CONVERSATION_DRAWER: 'toggle-conversation-drawer', // Toggle del drawer de conversaciones (backward compat)

  // Unified sidebar
  TOGGLE_SIDEBAR: 'toggle-sidebar', // Toggle del sidebar unificado (Chat + Docs)
  CONVERSATION_AUTO_TITLED: 'conversation-auto-titled', // Auto-rename "Nueva conversación" after first message

  // Processing status
  PROCESSING_STATUS_CHANGED: 'processing-status-changed', // SSE progress text (Buscando docs... / Generando...)
  TYPING_STATE_CHANGED: 'typing-state-changed', // isTyping bridged from ChatInputArea → ChatPanel

  // Panel coordination
  CLOSE_LEFT_PANELS: 'close-left-panels', // Cierra sidebars izquierdos (payload: { except: 'sidebar' | 'control' })

  // Mobile navigation
  FOCUS_SIDEBAR_SEARCH: 'focus-sidebar-search', // Focus en el input de búsqueda del sidebar

  // Conversation lifecycle
  NEW_CONVERSATION: 'new-conversation', // Reset filters/state when starting a new conversation
} as const;

export type EventName = (typeof EventNames)[keyof typeof EventNames];

/**
 * Hook for safely subscribing to EventBus events with automatic cleanup.
 * Prevents memory leaks from forgotten eventBus.off() calls.
 * Uses useRef to guarantee a stable handler reference across re-renders,
 * preventing listener accumulation (D04 fix).
 *
 * Usage:
 *   useEventListener(EventNames.TOGGLE_CONTROL_PANEL, () => setOpen(prev => !prev));
 */
import { useEffect, useRef } from 'react';

export function useEventListener(event: string, callback: EventCallback): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = (data?: any) => callbackRef.current(data);
    eventBus.on(event, handler);
    return () => {
      eventBus.off(event, handler);
    };
  }, [event]);
}
